import {
  Badge,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Heading,
} from '@apollo/orbit';
import React from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import {
  LocalTimestamp,
  RelativeTimestamp,
} from 'src/components/timestamp/Timestamp';
import {
  BuildPipelineTrackDetails,
  Launch,
  RegionDescription,
  RouterVersion,
  Status,
} from 'src/lib/graphqlTypes/types';

import { FederationVersion } from '../../FederationVersion';
import { VersionBadge } from '../versionBadge/VersionBadge';

export const RouterVersionCard = ({
  routerVersion,
  latestLaunch,
  federationVersion,
  availableTracks,
  graphRef,
  routerRegion,
}: {
  routerVersion?: Pick<RouterVersion, 'core' | 'status'> | null;
  latestLaunch?: Pick<Launch, 'id' | 'createdAt'> | null;
  federationVersion: string | null;
  availableTracks: Pick<
    BuildPipelineTrackDetails,
    '__typename' | 'compositionVersion' | 'minimumRouterVersion'
  >[];
  graphRef: GraphRef;
  routerRegion?: Pick<RegionDescription, 'code' | 'name'>;
}) => {
  const routerVersionCore = routerVersion?.core
    ? `V${routerVersion?.core}`
    : ''; // Core version of the router

  const getBadgeText = (status?: Status): string => {
    switch (status) {
      case Status.DEPRECATED:
        return 'DEPRECATED';
      case Status.NEXT:
        return 'EXPERIMENTAL VERSION';
      case Status.STABLE:
        return 'STABLE VERSION';
      case undefined:
        return 'NULL';
      default:
        return 'UNKNOWN STATUS';
    }
  };

  const { code, name } = routerRegion || {};

  return (
    <Card isDivided className="mb-4 md:mb-0 lg:mr-6">
      <CardHeader>
        <CardTitle>{routerVersionCore || '_'}</CardTitle>
        <CardDescription>Router version</CardDescription>
        <CardHeaderControl>
          <Badge variant="info">{getBadgeText(routerVersion?.status)}</Badge>
        </CardHeaderControl>
      </CardHeader>
      <CardBody>
        <div>
          Last updated:{' '}
          {latestLaunch?.id && (
            <LocalTimestamp
              value={latestLaunch?.createdAt}
              format="date-only"
            />
          )}
        </div>
        {routerVersion !== null && federationVersion && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center">
              <Heading size="lg" className="mr-2">
                Federation version
              </Heading>
              <VersionBadge version={federationVersion} />
            </div>

            <FederationVersion
              availableTracks={availableTracks}
              federationVersion={federationVersion}
              graphRef={graphRef}
            />
            {latestLaunch?.id && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-secondary">
                  Last updated{' '}
                  <RelativeTimestamp value={latestLaunch.createdAt} />
                </span>
              </div>
            )}
          </div>
        )}
        {code && name && (
          <div className="flex flex-col gap-1">
            <Heading size="lg">Region</Heading>
            <p>{code}</p>
            <p className="text-sm text-secondary">{name}</p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
